import React, { useMemo } from 'react';
import { Grid, Typography } from '@mui/material';

import { isEqual, toNumber } from 'lodash';

import { GridActionsColDef, GridColDef } from '@mui/x-data-grid';
import { Form } from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

import { formatCurrencyFloat, formatFloat, formatInt, formatPercentageString } from 'shared/utils/numberFormatters';
import { plugins } from 'forms/utils/formPlugins';
import { FormOptions, wrapForm } from 'components/shared/form';
import { formOptions } from 'forms/utils/formOptions';

import { useRootStore } from 'stores/RootStore';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { financingTermsFormFields, financingTermsFormHelpers } from './financingTermsFormConfig';
import { Table } from 'components/shared/Table';
import { INCOME_APPROACH_BASIS_TYPES, incomeApproachCalculations } from 'shared/calculations/incomeApproach';

interface FinancingTermsProps {
  form: Form;
}

interface FinancingTermsRow {
  id: number | string;
  name?: string;
  item?: string;
  borrower?: number;
  underwriter?: number;
  editable?: boolean;
  hypothetical?: number;
  hypotheticalEditable?: boolean;
  total?: boolean;
}
const formConfig: FormOptions = {
  fields: financingTermsFormFields,
  options: {
    ...formOptions,
    validateOnChange: true,
  },
  plugins,
};

const FinancingTerms: React.FC<FinancingTermsProps> = observer(({ form }) => {
  const { underwritingPageStore } = useRootStore();
  const { underwritingData = {} as UnderwritingOutput } = underwritingPageStore;
  const capRateField = form.$('financingTerms.borrower.capRate');
  const capRate = capRateField.value;
  const underwriterCapRateField = form.$('financingTerms.underwriter.capRate');
  const underwriterCapRate = underwriterCapRateField.value;
  const hypotheticalCapRateField = form.$('financingTerms.hypothetical.capRate');
  const hypotheticalCapRate = hypotheticalCapRateField.value;

  const netOperatingIncome = incomeApproachCalculations.calculateNetOperatingIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterNetOperatingIncome = incomeApproachCalculations.calculateNetOperatingIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );

  const hypotheticalNOIField = form.$('financingTerms.hypothetical.netOperatingIncome');
  const hypotheticalNOI = hypotheticalNOIField.value;
  const appraisedValue = useMemo(() => {
    return financingTermsFormHelpers.calculateAppraisedValue(netOperatingIncome, capRate);
  }, [capRate, netOperatingIncome]);
  const underwriterAppraisedValue = useMemo(() => {
    return financingTermsFormHelpers.calculateAppraisedValue(underwriterNetOperatingIncome, underwriterCapRate);
  }, [underwriterCapRate, underwriterNetOperatingIncome]);
  const hypotheticalAppraisedValue = useMemo(() => {
    return financingTermsFormHelpers.calculateAppraisedValue(hypotheticalNOI, hypotheticalCapRate);
  }, [hypotheticalCapRate, hypotheticalNOI]);

  const loanToValueField = form.$('financingTerms.borrower.loanToValue');
  const loanToValue = loanToValueField.value;
  const underwriterLoanToValueField = form.$('financingTerms.underwriter.loanToValue');
  const underwriterLoanToValue = underwriterLoanToValueField.value;
  const hypotheticalLoanToValueField = form.$('financingTerms.hypothetical.loanToValue');
  const hypotheticalLoanToValue = hypotheticalLoanToValueField.value;

  const loanAmount = useMemo(() => {
    return financingTermsFormHelpers.calculateLoanAmount(netOperatingIncome, capRate, loanToValue);
  }, [capRate, loanToValue, netOperatingIncome]);
  const underwriterLoanAmount = useMemo(() => {
    return financingTermsFormHelpers.calculateLoanAmount(
      underwriterNetOperatingIncome,
      underwriterCapRate,
      underwriterLoanToValue
    );
  }, [underwriterCapRate, underwriterLoanToValue, underwriterNetOperatingIncome]);
  const hypotheticalLoanAmount = useMemo(() => {
    return financingTermsFormHelpers.calculateLoanAmount(hypotheticalNOI, hypotheticalCapRate, hypotheticalLoanToValue);
  }, [hypotheticalCapRate, hypotheticalLoanToValue, hypotheticalNOI]);

  const interestRateField = form.$('financingTerms.borrower.interestRate');
  const interestRate = interestRateField.value;
  const underwriterInterestRateField = form.$('financingTerms.underwriter.interestRate');
  const underwriterInterestRate = underwriterInterestRateField.value;
  const hypotheticalInterestRateField = form.$('financingTerms.hypothetical.interestRate');
  const hypotheticalInterestRate = hypotheticalInterestRateField.value;

  const loanTermField = form.$('financingTerms.borrower.loanTerm');
  const loanTerm = loanTermField.value;
  const underwriterLoanTermField = form.$('financingTerms.underwriter.loanTerm');
  const underwriterLoanTerm = underwriterLoanTermField.value;
  const hypotheticalLoanTermField = form.$('financingTerms.hypothetical.loanTerm');
  const hypotheticalLoanTerm = hypotheticalLoanTermField.value;

  const debtService = useMemo(() => {
    return financingTermsFormHelpers.calculateDebtService(loanAmount, interestRate, loanTerm);
  }, [interestRate, loanAmount, loanTerm]);
  const underwriterDebtService = useMemo(() => {
    return financingTermsFormHelpers.calculateDebtService(
      underwriterLoanAmount,
      underwriterInterestRate,
      underwriterLoanTerm
    );
  }, [underwriterInterestRate, underwriterLoanAmount, underwriterLoanTerm]);
  const hypotheticalDebtService = useMemo(() => {
    return financingTermsFormHelpers.calculateDebtService(
      hypotheticalLoanAmount,
      hypotheticalInterestRate,
      hypotheticalLoanTerm
    );
  }, [hypotheticalInterestRate, hypotheticalLoanAmount, hypotheticalLoanTerm]);

  const debtServiceCoverageRatio = useMemo(() => {
    return financingTermsFormHelpers.calculateDebtServiceCoverageRatio(netOperatingIncome, debtService);
  }, [debtService, netOperatingIncome]);
  const underwriterDebtServiceCoverageRatio = useMemo(() => {
    return financingTermsFormHelpers.calculateDebtServiceCoverageRatio(
      underwriterNetOperatingIncome,
      underwriterDebtService
    );
  }, [underwriterDebtService, underwriterNetOperatingIncome]);
  const hypotheticalDebtServiceCoverageRatio = useMemo(() => {
    return financingTermsFormHelpers.calculateDebtServiceCoverageRatio(hypotheticalNOI, hypotheticalDebtService);
  }, [hypotheticalDebtService, hypotheticalNOI]);

  const valueFormatter = (value: any, row: any) => {
    const { name } = row;
    switch (name) {
      case 'interestRate':
      case 'capRate':
      case 'loanToValue':
        return formatPercentageString(toNumber(value));
      case 'netOperatingIncome':
      case 'appraisedValue':
      case 'loanAmount':
      case 'debtService':
        return formatCurrencyFloat(value);
      case 'loanTerm':
        return formatInt(value);
      case 'debtServiceCoverageRatio':
        return formatFloat(value);
      default:
        return value;
    }
  };

  const financingTermsColDefs = useMemo(
    (): (GridColDef | GridActionsColDef)[] => [
      {
        field: 'item',
        headerName: 'Item',
        editable: true,
        width: 250,
        sortable: false,
      },
      {
        field: 'borrower',
        headerName: 'Borrower',
        valueFormatter,
        editable: true,
        width: 200,
        flex: 1,
        sortable: false,
      },
      {
        field: 'underwriter',
        headerName: 'Underwriter',
        valueFormatter,
        editable: true,
        width: 200,
        flex: 1,
        sortable: false,
      },
      {
        field: 'hypothetical',
        headerName: 'Hypothetical',
        valueFormatter,
        editable: true,
        width: 200,
        flex: 1,
        sortable: false,
      },
    ],
    []
  );

  const processRowUpdate = (newRow: any, oldRow: any) => {
    if (newRow.isTotalRow || isEqual(newRow, oldRow)) {
      return oldRow;
    }
    const { name } = newRow;
    switch (name) {
      case 'capRate':
        capRateField.set(parseFloat(newRow.borrower));
        underwriterCapRateField.set(parseFloat(newRow.underwriter));
        hypotheticalCapRateField.set(parseFloat(newRow.hypothetical));
        return newRow;
      case 'loanToValue':
        loanToValueField.set(parseFloat(newRow.borrower));
        underwriterLoanToValueField.set(parseFloat(newRow.underwriter));
        hypotheticalLoanToValueField.set(parseFloat(newRow.hypothetical));
        return newRow;
      case 'interestRate':
        interestRateField.set(parseFloat(newRow.borrower));
        underwriterInterestRateField.set(parseFloat(newRow.underwriter));
        hypotheticalInterestRateField.set(parseFloat(newRow.hypothetical));
        return newRow;
      case 'loanTerm':
        loanTermField.set(parseFloat(newRow.borrower));
        underwriterLoanTermField.set(parseFloat(newRow.underwriter));
        hypotheticalLoanTermField.set(parseFloat(newRow.hypothetical));
        return newRow;
      case 'netOperatingIncome':
        hypotheticalNOIField.set(parseFloat(newRow.hypothetical));
        return newRow;
    }
    return oldRow;
  };

  const financingTermsRows = useMemo(
    (): FinancingTermsRow[] => [
      {
        id: 0,
        item: 'Net Operating Income',
        name: 'netOperatingIncome',
        borrower: netOperatingIncome,
        underwriter: underwriterNetOperatingIncome,
        editable: false,
        hypothetical: hypotheticalNOI,
        hypotheticalEditable: true,
      },
      {
        id: 1,
        item: 'Cap Rate',
        name: 'capRate',
        borrower: capRate,
        underwriter: underwriterCapRate,
        editable: true,
        hypothetical: hypotheticalCapRate,
        hypotheticalEditable: true,
      },
      {
        id: 3,
        item: 'Appraised Value',
        name: 'appraisedValue',
        borrower: appraisedValue,
        underwriter: underwriterAppraisedValue,
        editable: false,
        hypothetical: hypotheticalAppraisedValue,
        hypotheticalEditable: false,
      },
      {
        id: 4,
        item: 'Loan to Value (LTV)',
        name: 'loanToValue',
        borrower: loanToValue,
        underwriter: loanToValue,
        editable: true,
        hypothetical: hypotheticalLoanToValue,
        hypotheticalEditable: true,
      },
      {
        id: 5,
        item: 'Loan Amount',
        name: 'loanAmount',
        borrower: loanAmount,
        underwriter: underwriterLoanAmount,
        editable: false,
        hypothetical: hypotheticalLoanAmount,
        hypotheticalEditable: false,
      },
      {
        id: 6,
        item: 'Interest Rate',
        name: 'interestRate',
        borrower: interestRate,
        underwriter: underwriterInterestRate,
        editable: true,
        hypothetical: hypotheticalInterestRate,
        hypotheticalEditable: true,
      },
      {
        id: 7,
        item: 'Loan Term (Years)',
        name: 'loanTerm',
        borrower: loanTerm,
        underwriter: underwriterLoanTerm,
        editable: true,
        hypothetical: hypotheticalLoanTerm,
        hypotheticalEditable: true,
      },
      {
        id: 8,
        item: 'Debt Service',
        name: 'debtService',
        borrower: debtService,
        underwriter: underwriterDebtService,
        editable: false,
        hypothetical: hypotheticalDebtService,
        hypotheticalEditable: false,
        total: true,
      },
      {
        id: 9,
        item: 'Debt Service Coverage Ratio (DSCR)',
        name: 'debtServiceCoverageRatio',
        borrower: debtServiceCoverageRatio,
        underwriter: underwriterDebtServiceCoverageRatio,
        editable: false,
        hypothetical: hypotheticalDebtServiceCoverageRatio,
        hypotheticalEditable: false,
        total: true,
      },
    ],
    [
      appraisedValue,
      capRate,
      debtService,
      debtServiceCoverageRatio,
      hypotheticalAppraisedValue,
      hypotheticalCapRate,
      hypotheticalDebtService,
      hypotheticalDebtServiceCoverageRatio,
      hypotheticalInterestRate,
      hypotheticalLoanAmount,
      hypotheticalLoanTerm,
      hypotheticalLoanToValue,
      hypotheticalNOI,
      interestRate,
      loanAmount,
      loanTerm,
      loanToValue,
      netOperatingIncome,
      underwriterAppraisedValue,
      underwriterCapRate,
      underwriterDebtService,
      underwriterDebtServiceCoverageRatio,
      underwriterInterestRate,
      underwriterLoanAmount,
      underwriterLoanTerm,
      underwriterNetOperatingIncome,
    ]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Financing Terms</Typography>
      </Grid>
      <Grid
        item
        xs={9}
        sx={{
          minHeight: '150px',
          width: '100%',
          '& .total': {
            backgroundColor: 'rgb(245, 245, 245)',
            color: '#666',
            fontWeight: 'bold',
          },
        }}
      >
        <Table
          rows={financingTermsRows}
          columns={financingTermsColDefs}
          processRowUpdate={processRowUpdate}
          getCellClassName={(params) => {
            const {
              field,
              row: { editable, hypotheticalEditable, total },
            } = params;
            if (total) {
              return 'total';
            }
            if (field === 'hypothetical' && !hypotheticalEditable) {
              return 'disabled-cell';
            }
            if ((field === 'borrower' || field === 'underwriter') && !editable) {
              return 'disabled-cell';
            }
            if (field === 'item') {
              return 'disabled-cell';
            }
            return '';
          }}
          isCellEditable={(params) => {
            const { row, field } = params;
            if (field === 'hypothetical') {
              return row?.hypotheticalEditable ?? false;
            }
            return row?.editable ?? false;
          }}
        />
      </Grid>
    </Grid>
  );
});

export default wrapForm({
  ...formConfig,
})(FinancingTerms);
